import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Collapse,
    Typography,
    Chip
} from "@mui/material/";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import ExpandMore from "@mui/icons-material/ExpandMore";
import styles, { listItemButtonStyle } from "./styles";
import { strings } from "../../../../common/i18n";
import actions from "../../../../Store/actions";

const collectExpandableKeys = (data, path = []) => {
    const keys = {};
    Object.keys(data).forEach((key) => {
        const node = data[key];
        const nodePath = [...path, key];
        const nodeKey = nodePath.join("-");
        if (
            node.children &&
            Object.values(node.children).some(
                (child) => child.total && child.total > 0
            )
        ) {
            keys[nodeKey] = true;
            Object.assign(keys, collectExpandableKeys(node.children, nodePath));
        }
    });
    return keys;
};

const RecursiveList = ({ data, path = [], level = 0, openNodes, selectedNode, selectedChips, onNodeClick, onChipClick }) => {
    const theme = useTheme();
    return (
        <List component="div" disablePadding>
            {Object.keys(data).map((key) => {
                const node = data[key];

                if (!node.total || node.total === 0) {
                    return null;
                }

                const nodePath = [...path, key];
                const nodeKey = nodePath.join("-");
                const hasChildren =
                    node.children &&
                    Object.values(node.children).some(
                        (child) => child.total && child.total > 0
                    );
                const isOpen = openNodes[nodeKey];
                const isSelected = selectedNode === nodeKey;

                return (
                    <Box key={nodeKey}>
                        <ListItem
                            disablePadding
                            sx={listItemButtonStyle(isSelected, theme, level)}
                        >
                            <ListItemButton
                                onClick={() => onNodeClick(nodePath, node)}
                                sx={{
                                    backgroundColor: isSelected
                                        ? "rgba(250, 238, 203, 0.08)"
                                        : "inherit",
                                    "&:hover": {
                                        backgroundColor:
                                            "rgba(250, 238, 203, 0.08)"
                                    },
                                    color: isSelected
                                        ? "text.primary"
                                        : "inherit"
                                }}
                            >
                                <Typography sx={{ mx: 1 }} variant="body2">
                                    {node.total}
                                </Typography>
                                <ListItemText
                                    primary={
                                        <Typography
                                            variant="body2"
                                            fontWeight={
                                                isSelected ? "bold" : "normal"
                                            }
                                        >
                                            {strings(node.label)}
                                        </Typography>
                                    }
                                />
                                {node.pending > 0 && (
                                    <Chip
                                        label={node.pending}
                                        size="small"
                                        color="primary"
                                        variant={
                                            selectedChips[nodeKey]
                                                ? "filled"
                                                : "outlined"
                                        }
                                        sx={{ ml: 1 }}
                                        onClick={(e) =>
                                            onChipClick(nodePath, node, e)
                                        }
                                    />
                                )}
                                {hasChildren && (
                                    <ExpandMore
                                        sx={{
                                            transition: "transform 0.2s ease",
                                            transform: isOpen
                                                ? "rotate(-180deg)"
                                                : "rotate(0deg)"
                                        }}
                                    />
                                )}
                            </ListItemButton>
                        </ListItem>

                        {hasChildren && (
                            <Collapse in={isOpen} timeout={200} unmountOnExit>
                                <RecursiveList
                                    data={node.children}
                                    path={nodePath}
                                    level={level + 1}
                                    openNodes={openNodes}
                                    selectedNode={selectedNode}
                                    selectedChips={selectedChips}
                                    onNodeClick={onNodeClick}
                                    onChipClick={onChipClick}
                                />
                            </Collapse>
                        )}
                    </Box>
                );
            })}
        </List>
    );
};

export default ({ onToggleView }) => {
    const { auditReview } = useSelector((state) => state);
    const { altAssets, auditAssets, validatedVisibility } = auditReview;
    const dispatch = useDispatch();
    const deferTimeoutRef = useRef(null);

    // Defer the expensive filter + table re-render to the next tick so the
    // selection highlight paints immediately (React 17 batches otherwise).
    // A ref-tracked timeout lets a rapid follow-up click coalesce and the
    // cleanup effect cancel any pending update on unmount.
    const deferStatusUpdate = useCallback(
        (statusKey) => {
            clearTimeout(deferTimeoutRef.current);
            deferTimeoutRef.current = setTimeout(() => {
                if (statusKey) {
                    dispatch(actions.auditReview.create.statusSet([statusKey]));
                }
                onToggleView("table");
            }, 0);
        },
        [dispatch, onToggleView]
    );

    useEffect(() => () => clearTimeout(deferTimeoutRef.current), []);

    // Build hierarchy dynamically
    const hierarchy = useMemo(() => ({
        expected: {
            label: "expected",
            total: (auditAssets.total || 0) + (validatedVisibility.total || 0),
            pending:
                (auditAssets.seenPending || 0) +
                (auditAssets.excludedPending || 0) +
                //(auditAssets.reissuedPending || 0) +
                (auditAssets.unchecked || 0) +
                (validatedVisibility.pending || 0),
            children: {
                reconciled: {
                    label: "reconciled",
                    total:
                        (auditAssets.approved || 0) +
                        (validatedVisibility.approved || 0) +
                        (auditAssets.rejected || 0) +
                        (validatedVisibility.rejected || 0),
                    pending: auditAssets.pending || 0,
                    children: {
                        approved: {
                            label: "approved",
                            total:
                                (auditAssets.approved || 0) +
                                (validatedVisibility.approved || 0)
                        },
                        rejected: {
                            label: "rejected",
                            total:
                                (auditAssets.rejected || 0) +
                                (validatedVisibility.rejected || 0)
                        }
                    }
                },
                unreconciled: {
                    label: "unreconciled",
                    total:
                        (auditAssets.seenPending || 0) +
                        (auditAssets.excludedPending || 0) +
                        (auditAssets.unchecked || 0) +
                        //(auditAssets.reissuedPending || 0) +
                        (validatedVisibility.pending || 0),
                    pending:
                        (auditAssets.seenPending || 0) +
                        (auditAssets.unchecked || 0) +
                        //(auditAssets.reissuedPending || 0) +
                        (auditAssets.excludedPending || 0) +
                        (validatedVisibility.pending || 0),
                    children: {
                        unchecked: {
                            label: "unchecked",
                            total: auditAssets.unchecked || 0,
                            pending: auditAssets.unchecked || 0,
                            children: {}
                        },
                        validatedVisibility: {
                            label: "validatedVisibility",
                            total: validatedVisibility.total || 0,
                            pending: validatedVisibility.pending || 0,
                            children: {}
                        },
                        seen: {
                            label: "seen",
                            total: auditAssets.seenPending || 0,
                            pending: auditAssets.seenPending || 0,
                            children: {
                                autoverified: {
                                    label: "autoverified",
                                    total:
                                        auditAssets.autoVerifiedPending || 0,
                                    pending:
                                        auditAssets.autoVerifiedPending || 0
                                }
                            }
                        },
                        excluded: {
                            label: "excluded",
                            total: auditAssets.excludedPending || 0,
                            pending: auditAssets.excludedPending || 0
                        },
                        autoverified: {
                            label: "autoverified",
                            total: auditAssets.autoverified || 0,
                            pending:
                                auditAssets.expectedAutoVerifiedPending || 0
                        }
                    }
                },
                reissued: {
                    label: "recount",
                    total: auditAssets.reissued || 0,
                    pending: auditAssets.reissuedPending || 0,
                    children: {}
                },
                nonMandatory: {
                    label: "nonMandatory",
                    total: auditAssets.nonMandatoryTotal || 0,
                    pending: 0,
                    children: {}
                }
            }
        },
        unexpected: {
            label: "unexpected",
            total: altAssets.total || 0,
            pending: altAssets.pending || 0,
            children: {
                pending: {
                    label: "pending",
                    total: altAssets.pending || 0,
                    pending: altAssets.pending || 0
                },
                resolved: {
                    label: "resolved",
                    total: altAssets.resolved || 0,
                    pending: altAssets.resolvedPending || 0
                }
            }
        }
    }), [auditAssets, altAssets, validatedVisibility]);

    const [selectedChips, setSelectedChips] = useState({});
    const [openNodes, setOpenNodes] = useState(() =>
        collectExpandableKeys(hierarchy)
    );
    const [selectedNode, setSelectedNode] = useState(null);

    const handleNodeClick = useCallback(
        (path, node) => {
            const key = path.join("-");
            const hasChildren =
                node.children &&
                Object.values(node.children).some(
                    (child) => child.total && child.total > 0
                );

            if (hasChildren) {
                setOpenNodes((prev) => ({ ...prev, [key]: !prev[key] }));
            }

            // select this node and clear other chip selections
            setSelectedChips((prev) => {
                const newChips = {};
                Object.keys(prev).forEach(
                    (chipKey) => (newChips[chipKey] = false)
                );
                newChips[key] = true;
                return newChips;
            });

            setSelectedNode(key);

            let statusKey = null;
            if (path.length > 1) {
                const leaf = path[path.length - 1];
                if (
                    ["reconciled", "unreconciled", "decision"].includes(
                        leaf.toLowerCase()
                    )
                ) {
                    statusKey =
                        leaf === "unreconciled" ? "expectedPending" : path[0];
                } else {
                    statusKey =
                        path[0].toLowerCase() +
                        leaf.charAt(0).toUpperCase() +
                        leaf.slice(1);
                }
            } else {
                statusKey = path[0];
            }

            deferStatusUpdate(statusKey);
        },
        [deferStatusUpdate]
    );

    const handleChipClick = useCallback(
        (path, node, event) => {
            // stop the row click
            event.stopPropagation();

            const nodeKey = path.join("-");

            // highlight the row
            setSelectedNode(nodeKey);

            // set chip selection: clear others, mark this as selected
            setSelectedChips((prev) => {
                const newChips = {};
                Object.keys(prev).forEach(
                    (chipKey) => (newChips[chipKey] = false)
                );
                newChips[nodeKey] = true;
                return newChips;
            });

            // compute pending status key (same logic as before but add "Pending")
            const leaf = path[path.length - 1];
            let statusKey = null;
            if (path.length > 1) {
                if (
                    ["reconciled", "unreconciled", "unexpected"].includes(
                        leaf.toLowerCase()
                    )
                ) {
                    statusKey =
                        leaf === "unreconciled" ? "expectedPending" : path[0];
                } else {
                    if (leaf !== "pending")
                        statusKey =
                            path[0].toLowerCase() +
                            leaf.charAt(0).toUpperCase() +
                            leaf.slice(1) +
                            "Pending";
                }
            } else {
                statusKey = path[0] + "Pending";
            }

            deferStatusUpdate(statusKey);
        },
        [deferStatusUpdate]
    );

    return (
        <RecursiveList
            data={hierarchy}
            openNodes={openNodes}
            selectedNode={selectedNode}
            selectedChips={selectedChips}
            onNodeClick={handleNodeClick}
            onChipClick={handleChipClick}
        />
    );
};
